/**
 * The short chime played when a notification arrives live (any portal's bell).
 * Generated with the Web Audio API, so there's no sound file to ship or load.
 *
 * Browsers only allow audio after the user has interacted with the page, so
 * the audio context is unlocked on the first click / key press; a chime
 * before that is skipped silently. At most one chime per MIN_GAP_MS, so a
 * burst of notifications doesn't stack sounds.
 */

const MIN_GAP_MS = 1_500;

let context: AudioContext | null = null;
let lastPlayedAt = 0;

function getContext(): AudioContext | null {
  if (context) return context;
  const Ctor = typeof window !== "undefined"
    ? window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    : undefined;
  if (!Ctor) return null;
  try {
    context = new Ctor();
  } catch {
    return null;
  }
  return context;
}

function unlock() {
  const ctx = getContext();
  if (ctx && ctx.state === "suspended") void ctx.resume().catch(() => {});
}

if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", unlock, { once: true, capture: true });
  window.addEventListener("keydown", unlock, { once: true, capture: true });
}

// A struck bell: the note plus quieter overtones that die away faster than it
// does — that fast-fading shimmer is what makes it read as a chime rather
// than a beep. The last overtone is slightly off-harmonic, like a real bell.
const BELL_PARTIALS = [
  { ratio: 1, level: 1, decay: 1 },
  { ratio: 2, level: 0.45, decay: 0.55 },
  { ratio: 3, level: 0.18, decay: 0.35 },
  { ratio: 4.18, level: 0.1, decay: 0.2 },
];

function bell(ctx: AudioContext, output: AudioNode, frequency: number, start: number, duration: number) {
  for (const { ratio, level, decay } of BELL_PARTIALS) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const end = start + duration * decay;
    osc.type = "sine";
    osc.frequency.value = frequency * ratio;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(level, start + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(gain).connect(output);
    osc.start(start);
    osc.stop(end + 0.02);
  }
}

/** Two-note bell chime (C6 then G6) — clearly a notification, soft enough not to startle. No-op where audio isn't available or allowed yet. */
export function playNotificationSound(): void {
  const now = Date.now();
  if (now - lastPlayedAt < MIN_GAP_MS) return;
  const ctx = getContext();
  if (!ctx || ctx.state !== "running") return;
  lastPlayedAt = now;
  try {
    const t = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.16;
    master.connect(ctx.destination);
    bell(ctx, master, 1046.5, t, 0.9);
    bell(ctx, master, 1568, t + 0.14, 1.2);
  } catch {
    // audio is a nicety — never let it break the notification itself
  }
}
